import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Phone, Mail, MapPin, Plus, CheckCircle2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { inr, statusLabel, STATUS_COLORS, PIPELINE } from "../../lib/site";

const FU_TYPES = ["call", "whatsapp", "email", "meeting", "document_collection", "site_visit", "other"];

export default function LeadDetail() {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [note, setNote] = useState("");
  const [fu, setFu] = useState({ date: "", time: "", type: "call", notes: "" });

  const load = useCallback(() => {
    api.get(`/admin/leads/${id}`).then((r) => setLead(r.data)).catch(() => setLead(false));
  }, [id]);

  useEffect(() => {
    load();
    api.get("/admin/employees").then((r) => setEmployees(r.data.filter((e) => ["manager", "sales_executive"].includes(e.role)))).catch(() => {});
  }, [load]);

  const patch = async (body) => {
    try {
      const { data } = await api.patch(`/admin/leads/${id}`, body);
      setLead(data);
      toast.success("Lead updated");
    } catch (err) { toast.error(apiError(err)); }
  };

  const addFollowUp = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/admin/leads/${id}/followups`, fu);
      setLead(data);
      setFu({ date: "", time: "", type: "call", notes: "" });
      toast.success("Follow-up scheduled");
    } catch (err) { toast.error(apiError(err)); }
  };

  const markDone = async (fuId) => {
    await api.patch(`/admin/leads/${id}/followups/${fuId}`);
    load();
  };

  if (lead === null) return <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-brand-red" size={28} /></div>;
  if (lead === false) return <div className="py-24 text-center text-brand-muted">Lead not found or not accessible.</div>;

  const field = (label, value) => value ? (
    <div><p className="text-[10px] uppercase tracking-wider font-bold text-brand-muted">{label}</p><p className="text-sm font-semibold text-brand-dark mt-0.5">{value}</p></div>
  ) : null;

  return (
    <div data-testid="lead-detail-page">
      <Link to="/admin/leads" data-testid="back-to-leads" className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-muted hover:text-brand-dark mb-5"><ArrowLeft size={14} /> All Leads</Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl font-extrabold text-brand-dark" data-testid="lead-name">{lead.name}</h1>
            <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${STATUS_COLORS[lead.status]}`}>{statusLabel(lead.status)}</span>
          </div>
          <p className="text-sm text-brand-muted mt-1">{lead.lead_id} · via {lead.source || "website"} · {new Date(lead.created_at).toLocaleString("en-IN")}</p>
        </div>
        <div className="flex gap-2">
          <a href={`tel:${lead.mobile}`} data-testid="lead-call-btn" className="btn-outline !px-4 !py-2 !text-xs"><Phone size={13} /> Call</a>
          <a href={`https://wa.me/${(lead.mobile || "").replace(/\D/g, "").replace(/^(\d{10})$/, "91$1")}`} target="_blank" rel="noreferrer" data-testid="lead-whatsapp-btn" className="btn-outline !px-4 !py-2 !text-xs !border-[#1FA855]/40 !text-[#1FA855]"><MessageCircle size={13} /> WhatsApp</a>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white rounded-xl border border-black/5 p-5" data-testid="lead-info-card">
            <h3 className="font-heading font-bold text-sm text-brand-dark mb-4">Lead Details</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {field("Mobile", lead.mobile)}
              {field("Email", lead.email)}
              {field("City", lead.city)}
              {field("Loan Type", lead.loan_type)}
              {field("Amount", lead.amount ? inr(lead.amount) : "")}
              {field("Purpose", lead.purpose)}
              {field("Employment", lead.employment)}
              {field("Monthly Income", lead.income ? inr(lead.income) : "")}
              {field("Existing EMI", lead.existing_emi ? inr(lead.existing_emi) : "")}
              {field("CIBIL", lead.cibil)}
              {field("Employer", lead.employer)}
              {field("Business", lead.business_name)}
              {field("Turnover", lead.turnover ? inr(lead.turnover) : "")}
              {field("Property", lead.property_type)}
              {field("Lead Score", `${lead.score} (${lead.score_label})`)}
              {field("UTM Source", lead.utm_source)}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-black/5 p-5" data-testid="lead-timeline">
            <h3 className="font-heading font-bold text-sm text-brand-dark mb-4">Activity Timeline</h3>
            <div className="space-y-4">
              {[...(lead.activities || [])].reverse().map((a) => (
                <div key={a.id} className="flex gap-3">
                  <span className="mt-1 w-2 h-2 rounded-full bg-brand-red shrink-0" />
                  <div>
                    <p className="text-sm text-brand-dark">{a.text}</p>
                    <p className="text-xs text-brand-muted mt-0.5">{a.by} · {new Date(a.at).toLocaleString("en-IN")}</p>
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); if (note.trim()) { patch({ notes: note }); setNote(""); } }} className="mt-5 flex gap-2">
              <input className="input-site" placeholder="Add a note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="lead-note-input" />
              <button type="submit" data-testid="lead-note-add-btn" className="btn-primary !px-4 !py-2 !text-xs shrink-0">Add</button>
            </form>
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-black/5 p-5">
            <h3 className="font-heading font-bold text-sm text-brand-dark mb-4">Manage</h3>
            <label className="text-[10px] uppercase tracking-wider font-bold text-brand-muted">Pipeline Stage</label>
            <select className="input-site mt-1.5 mb-4" value={lead.status} onChange={(e) => patch({ status: e.target.value })} data-testid="lead-status-select">
              {PIPELINE.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
            <label className="text-[10px] uppercase tracking-wider font-bold text-brand-muted">Priority</label>
            <select className="input-site mt-1.5 mb-4" value={lead.priority} onChange={(e) => patch({ priority: e.target.value })} data-testid="lead-priority-select">
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
            </select>
            <label className="text-[10px] uppercase tracking-wider font-bold text-brand-muted">Assigned To</label>
            <select className="input-site mt-1.5" value={lead.assigned_to || ""} onChange={(e) => patch({ assigned_to: e.target.value })} data-testid="lead-assign-select">
              <option value="">Unassigned</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>

          <div className="bg-white rounded-xl border border-black/5 p-5" data-testid="followups-card">
            <h3 className="font-heading font-bold text-sm text-brand-dark mb-4">Follow-ups</h3>
            <div className="space-y-3 mb-4">
              {(lead.follow_ups || []).map((f) => (
                <div key={f.id} className={`rounded-lg border p-3 text-sm ${f.done ? "border-black/5 opacity-50" : "border-black/10"}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-brand-dark text-xs uppercase tracking-wide">{f.type.replace("_", " ")}</span>
                    {!f.done && <button onClick={() => markDone(f.id)} data-testid={`followup-done-${f.id}`} className="text-emerald-600 hover:opacity-80"><CheckCircle2 size={15} /></button>}
                  </div>
                  <p className="text-xs text-brand-muted mt-1">{f.date} {f.time} · by {f.by}</p>
                  {f.notes && <p className="text-xs text-brand-dark/70 mt-1">{f.notes}</p>}
                </div>
              ))}
              {(lead.follow_ups || []).length === 0 && <p className="text-xs text-brand-muted">No follow-ups scheduled.</p>}
            </div>
            <form onSubmit={addFollowUp} className="space-y-2.5" data-testid="followup-form">
              <div className="grid grid-cols-2 gap-2.5">
                <input required type="date" className="input-site !py-2" value={fu.date} onChange={(e) => setFu({ ...fu, date: e.target.value })} data-testid="followup-date" aria-label="Follow-up date" />
                <input type="time" className="input-site !py-2" value={fu.time} onChange={(e) => setFu({ ...fu, time: e.target.value })} data-testid="followup-time" aria-label="Follow-up time" />
              </div>
              <select className="input-site !py-2" value={fu.type} onChange={(e) => setFu({ ...fu, type: e.target.value })} data-testid="followup-type">
                {FU_TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
              </select>
              <input className="input-site !py-2" placeholder="Notes" value={fu.notes} onChange={(e) => setFu({ ...fu, notes: e.target.value })} data-testid="followup-notes" />
              <button type="submit" className="btn-primary w-full !py-2 !text-xs" data-testid="followup-submit-btn"><Plus size={13} /> Schedule Follow-up</button>
            </form>
          </div>

          <div className="bg-white rounded-xl border border-black/5 p-5 text-sm">
            <div className="flex items-center gap-2 text-brand-dark"><Mail size={14} className="text-brand-red" /> <span className="font-semibold">{lead.email || "No email"}</span></div>
            <div className="flex items-center gap-2 text-brand-dark mt-2.5"><MapPin size={14} className="text-brand-red" /> <span className="font-semibold">{lead.city || "No city"}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
